import React, { useState } from 'react';
import _get from 'lodash/get';
import { Link } from 'react-router-dom';
import {
  Button,
  Dialog,
  Slide,
  Typography,
  makeStyles,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Mixpanel from 'utils/mixpanelService';
import { getImageFile } from 'utils/stringUtils';
import AppContainer from 'components/AppContainer';
import ArticleInformation from 'containers/Article/components/ArticleInformation';
import useSiteCopySelector from 'components/useSiteCopySelector';
import AboutThePerson from 'components/AboutThePerson';
import { getResourceName, getResourceType } from 'containers/Series/utils';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    display: 'flex',
  },
  appContainerRoot: {
    marginTop: theme.spacing(4),
  },
  imageContainer: {
    backgroundImage: props => `url(${props.imageSrc})`,
    backgroundPosition: 'center center',
    width: props => (props.small ? 60 : '100%'),
    height: props => (props.small ? 60 : 110),
    '& img': {
      objectFit: 'cover',
      height: '100%',
    },
  },
  blurContainer: {
    padding: '10px 0px',
    backdropFilter: 'blur(4px)',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    '& img': {
      objectFit: 'cover',
      height: '100%',
    },
  },
  detailsContainer: {
    marginTop: theme.spacing(2),
    '& h6': {
      fontSize: props => props.small && '1rem',
    },
  },
  descriptionPreview: {
    display: '-webkit-box',
    '-webkit-line-clamp': '3',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  button: {
    borderRadius: '20px',
    marginTop: theme.spacing(2),
    width: '100%',
  },
  closeIcon: {
    position: 'absolute',
    top: 0,
    right: 5,
  },
}));

export const RecommendedArticlePreview = ({
  item,
  coursesSiteCopy,
  trackingProps,
  small,
  isDashboard,
}) => {
  const imageSrc = getImageFile(item.fields);
  const classes = useStyles({ small, imageSrc });
  const [show, setShow] = useState(false);
  const [resourceItemPageSiteCopy] = useSiteCopySelector([
    'resource-item-page',
  ]);

  const onClose = () => {
    setShow(false);
  };

  const onReadArticle = () => {
    setShow(true);

    const eventName = isDashboard
      ? 'Course Dashboard - Resource - Viewed'
      : 'Course - Recommended Resource - Viewed';

    Mixpanel.track(eventName, {
      ...trackingProps,
      name: getResourceName(item),
      type: getResourceType(item),
    });
  };

  const renderLinkNodeEntry = ({
    link,
    value,
    isLinkActive,
    onCMLinkClick,
  }) => {
    if (!isLinkActive) {
      return <span>{value}</span>;
    }

    return (
      <Link
        to={link}
        onClick={onCMLinkClick}
        target="_blank"
        className="embedded-link"
      >
        {value}
      </Link>
    );
  };

  const isCredibleBlog = _get(item, 'fields.blog', false);
  const person = _get(item, 'fields.person');
  const showPerson = _get(person, '0.fields.reviewStatus') === 'Accepted';

  return (
    <div className={classes.root}>
      <div className={classes.imageContainer}>
        <div className={classes.blurContainer}>
          <img loading="lazy" src={imageSrc} alt={item.fields.name} />
        </div>
      </div>
      <div className={classes.detailsContainer}>
        <Typography color="textPrimary" variant="subtitle2">
          {item.fields.name}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          onClick={onReadArticle}
        >
          {_get(coursesSiteCopy, 'pageCopy.readArticle')}
        </Button>
      </div>
      <Dialog
        fullScreen
        open={show}
        onClose={onClose}
        TransitionComponent={Slide}
      >
        <IconButton
          color="inherit"
          onClick={onClose}
          aria-label="Close"
          size="medium"
          className={classes.closeIcon}
        >
          <CloseIcon />
        </IconButton>
        <AppContainer
          customClasses={{ root: classes.appContainerRoot }}
          justify="start"
        >
          <ArticleInformation
            data={[item]}
            hasUserReviews={false}
            showMobileNode={false}
            isCredibleBlog={isCredibleBlog}
            renderLinkNodeEntry={renderLinkNodeEntry}
            linkAsPlainText
          />
          {showPerson && (
            <AboutThePerson
              data={person}
              heading={_get(
                resourceItemPageSiteCopy,
                'pageCopy.articlePeopleHeader',
              )}
              pageData={[item]}
            />
          )}
        </AppContainer>
      </Dialog>
    </div>
  );
};
